import { tallyTokens } from "../../theme.tsx";
import * as React from "react";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import { Dashboard } from "../../store/types.tsx";
import {
  conversationActions,
  deleteDashboard,
  patchDashboard,
  selectDashboardAction,
} from "../../store/conversation.tsx";
import RowMenu from "./RowMenu.tsx";
import {
  RenameActionIcon,
  DeleteActionIcon,
} from "../icons/DrawerNavIcons.tsx";

interface DashboardDrawerSectionProps {
  dashboards: Dashboard[];
  title: string;
  closeDrawer: () => void;
  onDashboardSelected?: () => void;
}

const DashboardDrawerSection: React.FC<DashboardDrawerSectionProps> = ({
  dashboards,
  title,
  closeDrawer,
  onDashboardSelected,
}) => {
  const dispatch = useDispatch();
  const [menuDashboard, setMenuDashboard] = React.useState<Dashboard | null>(
    null,
  );
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState("");
  const { selectedDashboard, selectedView } = useTypedSelector(
    (state) => state.conversationState,
  );
  const handleSelectDashboard = (dashboardId: string) => {
    closeDrawer();
    onDashboardSelected?.();
    dispatch(conversationActions.setSelectedView("dashboard"));
    dispatch(selectDashboardAction(dashboardId) as any);
  };

  // Takes the dashboard directly: the menu sets it and opens the dialog in the
  // same tick, so reading it back from state here would see the previous value.
  const handleOpenRename = (dashboard: Dashboard) => {
    setMenuDashboard(dashboard);
    setRenameValue(dashboard.title);
    setRenameOpen(true);
  };

  const handleConfirmRename = async () => {
    if (!menuDashboard || renameValue.trim() === "") {
      return;
    }

    await dispatch(patchDashboard(menuDashboard.id, renameValue.trim()) as any);
    setRenameOpen(false);
    setMenuDashboard(null);
  };

  const handleOpenDelete = (dashboard: Dashboard) => {
    setMenuDashboard(dashboard);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!menuDashboard) return;

    await dispatch(deleteDashboard(menuDashboard.id) as any);
    setDeleteOpen(false);
    setMenuDashboard(null);
  };

  const handleCloseDialogs = () => {
    setRenameOpen(false);
    setDeleteOpen(false);
    setMenuDashboard(null);
  };

  return (
    <Box className="conversation-drawer-section">
      <h4>{title}</h4>
      <Box sx={{ clear: "both" }}>
        {dashboards.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: tallyTokens.ink2,
              py: "6px",
            }}
          >
            {i18n._(msg`No dashboards yet`)}
          </Typography>
        ) : (
          <List>
            {dashboards.map((dashboard) => {
              const isSelected =
                selectedView === "dashboard" &&
                selectedDashboard === dashboard.id;

              return (
                <ListItem
                  key={dashboard.id}
                  disablePadding
                  sx={{
                    borderRadius: "25px",
                    transition: "all 0.2s ease",
                    overflow: "visible",
                  }}
                >
                  <Tooltip
                    title={
                      dashboard.tile_count === 1
                        ? i18n._(msg`${dashboard.tile_count} tile`)
                        : i18n._(msg`${dashboard.tile_count} tiles`)
                    }
                    arrow
                    placement="right"
                  >
                    <Box
                      className="conversation-section-full-width"
                      sx={[
                        {
                          borderRadius: `${tallyTokens.radius.sm}px`,
                          color: tallyTokens.ink2,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                        },
                        {
                          "&:hover": {
                            backgroundColor: tallyTokens.surface3,
                            color: tallyTokens.ink,
                          },
                        },
                        // Matches the selected conversation: a raised chip.
                        isSelected && {
                          backgroundColor: tallyTokens.surface,
                          color: tallyTokens.ink,
                          fontWeight: 600,
                          boxShadow: tallyTokens.shadow.sm,
                          borderRadius: `${tallyTokens.radius.sm}px`,
                          "&:hover": {
                            backgroundColor: tallyTokens.surface,
                            color: tallyTokens.ink,
                          },
                        },
                      ]}
                    >
                      <Box
                        sx={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "3px",
                          backgroundColor: tallyTokens.brand,
                          opacity: 0.55,
                          flexShrink: 0,
                        }}
                      />
                      <ListItemButton
                        disableRipple
                        onClick={() => handleSelectDashboard(dashboard.id)}
                        sx={{
                          flex: 1,
                          borderRadius: "25px",
                          // Match the conversation rows: the wrapper supplies the
                          // inset, so drop MUI's default 16px side padding.
                          px: 0,
                          py: "5px",
                          minHeight: 0,
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <ListItemText
                          primary={dashboard.title}
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            my: 0,
                            "& .MuiListItemText-primary": {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: "0.875rem",
                              lineHeight: 1.35,
                              color: "inherit",
                            },
                          }}
                        />
                      </ListItemButton>
                      {isSelected && (
                        <RowMenu
                          ariaLabel={i18n._(msg`Dashboard options`)}
                          actions={[
                            {
                              id: "rename",
                              icon: <RenameActionIcon />,
                              label: i18n._(msg`Rename`),
                              onClick: () => handleOpenRename(dashboard),
                            },
                            {
                              id: "delete",
                              icon: <DeleteActionIcon />,
                              label: i18n._(msg`Delete`),
                              destructive: true,
                              onClick: () => handleOpenDelete(dashboard),
                            },
                          ]}
                        />
                      )}
                    </Box>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      <Dialog
        open={renameOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{i18n._(msg`Rename dashboard`)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={i18n._(msg`Dashboard title`)}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{i18n._(msg`Cancel`)}</Button>
          <Button
            onClick={handleConfirmRename}
            variant="contained"
            disabled={renameValue.trim() === ""}
          >
            {i18n._(msg`Save`)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{i18n._(msg`Delete dashboard?`)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {menuDashboard
              ? i18n._(
                  msg`This will remove "${menuDashboard.title}" and all of its tiles.`,
                )
              : i18n._(
                  msg`This will remove this dashboard and all of its tiles.`,
                )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>{i18n._(msg`Cancel`)}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            {i18n._(msg`Delete`)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardDrawerSection;
